// Vista kanban do pipeline comercial — colunas por etapa com arrastar-e-soltar.
//
// Redesenho de 01/10/2026 (pedido do Lucas: trazer o padrão dos CRMs de
// referência). O que mudou, e de onde veio:
//
//  • **A coluna rola sozinha e o cabeçalho fica parado** (HubSpot). Com 169
//    cartões no Follow-up, a página inteira rolava e o nome da etapa sumia.
//  • **A coluna mostra 30 cartões e o resto sob demanda.** 169 cartões de uma vez
//    não se leem — e o cabeçalho já diz a contagem e o valor do todo.
//  • **O cartão diz se o negócio está parado** (o "rotting" do Pipedrive): faixa
//    lateral e "há N dias" em verde, âmbar ou vermelho.
//  • **O cartão diz se há próximo passo** — e avisa quando não há.
//  • **Dono em avatar**, valor em destaque, ações só ao passar o mouse: o cartão
//    antigo tinha dois botões fixos em cada um dos 479 negócios.
//  • **Ganhar e Perder viram alvos no rodapé enquanto se arrasta** (Pipedrive),
//    em vez de exigir achar a faixa estreita da coluna recolhida.
//
//  • **O cabeçalho da coluna é o funil** (01/10/2026, tarde — Lucas: "muito
//    cabeçalho sobre cabeçalho e o funil lá embaixo espremido"). Havia uma faixa
//    de setas acima do quadro repetindo a contagem e o valor de cada coluna. A
//    faixa saiu; a seta virou o próprio cabeçalho, com nome, quantidade e valor
//    curto (o exato e o ponderado ficam na dica).
//  • **O quadro ocupa o que sobra da tela**: a altura das colunas é medida a
//    partir de onde o quadro começa, em vez de um desconto fixo.
//
// Colunas "fechadas" (ganho/perdido) continuam recolhidas por padrão.
// Extraído de ComercialPipeline.tsx em 2026-05-23 (frontend wave 3B).
import { useLayoutEffect, useRef, useState } from 'react';
import { Button, Tag, Typography, Tooltip, Skeleton } from 'antd';
import {
  BarChartOutlined, DeleteOutlined, EyeOutlined, CheckOutlined, CloseOutlined,
} from '@ant-design/icons';
import { Proposta } from './types';
import { fmt, diasAte, urgenciaAbertura, STATUS_LABELS, STATUS_COLORS, COLUNAS_KANBAN } from './utils';
import {
  COR_DO_NIVEL, nivelParado, ordenarNegocios, valorDoNegocio, valorPonderado, valorCompacto, ehAberto,
  type Ordenacao, type ProximoPasso,
} from './saudeNegocio';
import { AvatarDono, SeloParado, SeloPasso } from './pecas';

const { Text } = Typography;

/** Quantos cartões a coluna mostra de cada vez. */
const LOTE = 30;

const CSS = `
.pk-quadro { display: grid; gap: 10px; align-items: start; overflow-x: auto; padding-bottom: 6px; }
.pk-col { display: flex; flex-direction: column; min-width: 0;
  max-height: var(--pk-alt, max(420px, calc(100vh - 190px))); }
/* O cabeçalho é uma seta que avança 10px sobre o vão do grid e encosta na
   coluna seguinte: lado a lado, os cabeçalhos desenham o funil. */
.pk-cab { flex: none; margin-right: -10px; padding: 7px 22px 7px 24px; background: var(--pk-fundo);
  clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%);
  transition: background .12s; }
.pk-col:first-child .pk-cab { padding-left: 12px; border-radius: 6px 0 0 6px;
  clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%); }
.pk-cab-n { font-size: 12px; color: #0E1433; margin-top: 1px; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; font-variant-numeric: tabular-nums; }
.pk-corpo { flex: 1 1 auto; overflow-y: auto; margin-top: 6px; padding: 8px; display: flex; flex-direction: column;
  gap: 6px; min-height: 90px; background: #F4F5F8; border: 1px solid #E6E8EF; border-radius: 8px;
  transition: background .12s, border-color .12s; }
.pk-col.pk-alvo .pk-corpo { background: #EAF0FF; border-color: #0930D0; }
.pk-cartao { position: relative; background: #fff; border: 1px solid #E6E8EF; border-left: 3px solid var(--pk-faixa);
  border-radius: 6px; padding: 8px 9px 7px; cursor: pointer; transition: box-shadow .12s, border-color .12s; }
.pk-cartao:hover { box-shadow: 0 2px 8px rgba(14,20,51,.10); border-color: #C9CEDD; border-left-color: var(--pk-faixa); }
.pk-cartao:focus-visible { outline: 2px solid #0930D0; outline-offset: 1px; }
.pk-cartao.pk-arrastando { opacity: .45; }
.pk-acoes { position: absolute; top: 4px; right: 4px; display: flex; gap: 1px; opacity: 0; transition: opacity .1s;
  background: #fff; border-radius: 4px; box-shadow: 0 0 0 1px #E6E8EF; }
.pk-cartao:hover .pk-acoes, .pk-cartao:focus-within .pk-acoes { opacity: 1; }
/* No toque não existe "passar o mouse": as ações ficam sempre à vista. */
@media (hover: none) { .pk-acoes { opacity: 1; } }
.pk-titulo { font-size: 12.5px; font-weight: 600; color: #0E1433; line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.pk-cliente { font-size: 11px; color: #5A6378; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pk-fechada { cursor: pointer; min-height: 300px; border-radius: 8px; display: flex; flex-direction: column;
  align-items: center; padding: 10px 4px; border: 1px solid #E6E8EF; background: #FAFBFD; transition: background .12s; }
.pk-fechada.pk-alvo { background: #EAF0FF; border-color: #0930D0; }
.pk-soltar { position: fixed; left: 50%; bottom: 18px; transform: translateX(-50%); z-index: 1000; display: flex; gap: 10px;
  padding: 8px; border-radius: 10px; background: #0E1433; box-shadow: 0 8px 28px rgba(14,20,51,.35); }
.pk-soltar > div { min-width: 190px; padding: 12px 18px; border-radius: 7px; border: 1.5px dashed rgba(255,255,255,.45);
  color: #fff; font-size: 13px; font-weight: 600; text-align: center; transition: background .1s; }
.pk-soltar > div.pk-sobre-ganho { background: #0a7f4a; border-style: solid; border-color: #0a7f4a; }
.pk-soltar > div.pk-sobre-perdido { background: #B91626; border-style: solid; border-color: #B91626; }
@media (max-width: 768px) { .pk-soltar > div { min-width: 120px; padding: 10px; } }
`;

export function KanbanPipeline({
  propostas, passos, loading, ordenacao, onAbrir, onEspiar, onMudarStatus, onIrComposicao, onExcluir,
}: {
  propostas: Proposta[];
  /** Próximo passo agendado por negócio (id → resumo). */
  passos: Record<number, ProximoPasso>;
  loading: boolean;
  ordenacao: Ordenacao;
  onAbrir: (p: Proposta) => void;
  onEspiar: (p: Proposta) => void;
  onMudarStatus: (id: number, novo: string) => void;
  onIrComposicao: (p: Proposta) => void;
  onExcluir: (id: number) => void;
}) {
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [hoverCol, setHoverCol] = useState<string | null>(null);
  const [visiveis, setVisiveis] = useState<Record<string, number>>({});
  // Altura das colunas = o que sobra da tela abaixo de onde o quadro começa.
  // Um desconto fixo ("100vh - 150px") errava para os dois lados: com a barra
  // de filtros em uma linha sobrava vão, com ela quebrada em duas o quadro
  // passava do fim da tela.
  const quadro = useRef<HTMLDivElement>(null);
  const [altura, setAltura] = useState<number | null>(null);
  useLayoutEffect(() => {
    const medir = () => {
      const el = quadro.current;
      if (!el) return;
      const topo = el.getBoundingClientRect().top + window.scrollY;
      // 28 = respiro do rodapé + a folga da barra de rolagem lateral do quadro:
      // com menos, a página ganhava uma rolagem de poucos pixels.
      setAltura(Math.max(420, Math.round(window.innerHeight - topo - 28)));
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [loading, passos]);
  // Colunas "fechadas" (ganho/perdido) começam colapsadas por padrão — viram
  // faixas verticais finas com label + contagem. Click expande. Preferência
  // persistida em localStorage pra cada uma poder ser independente.
  const [colapsadas, setColapsadas] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('pipeline_colunas_colapsadas');
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* preferência ilegível: vale o padrão */ }
    return new Set(['ganho', 'perdido']);
  });
  function toggleColapsada(status: string) {
    setColapsadas(prev => {
      const n = new Set(prev);
      if (n.has(status)) n.delete(status); else n.add(status);
      try { localStorage.setItem('pipeline_colunas_colapsadas', JSON.stringify(Array.from(n))); } catch { /* sem storage */ }
      return n;
    });
  }

  function soltar(status: string) {
    if (arrastando) onMudarStatus(arrastando, status);
    setArrastando(null); setHoverCol(null);
  }
  const alvo = (status: string) => ({
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); if (hoverCol !== status) setHoverCol(status); },
    onDragLeave: () => setHoverCol(h => (h === status ? null : h)),
    onDrop: (e: React.DragEvent) => { e.preventDefault(); soltar(status); },
  });

  if (loading) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 10 }}>
        {[0, 1, 2, 3, 4].map(i => <Skeleton key={i} active paragraph={{ rows: 6 }} />)}
      </div>
    );
  }

  // Colunas colapsadas ocupam 44px (faixa fina); abertas dividem o resto em
  // partes iguais, com piso de 232px — abaixo disso o quadro rola de lado em
  // vez de espremer o cartão.
  const gridCols = COLUNAS_KANBAN.map(s => colapsadas.has(s) ? '44px' : 'minmax(232px, 1fr)').join(' ');

  return (
    <>
      <style>{CSS}</style>
      <div ref={quadro} className="pk-quadro"
        style={{ gridTemplateColumns: gridCols, ['--pk-alt' as any]: altura ? `${altura}px` : undefined }}>
        {COLUNAS_KANBAN.map(status => {
          const itens = ordenarNegocios(propostas.filter(p => p.status === status), ordenacao);
          const total = itens.reduce((s, p) => s + valorDoNegocio(p), 0);
          const ponderado = itens.reduce((s, p) => s + valorPonderado(p), 0);
          const st = STATUS_COLORS[status];
          const ehAlvo = hoverCol === status;

          if (colapsadas.has(status)) {
            return (
              <div key={status} {...alvo(status)} onClick={() => toggleColapsada(status)}
                role="button" tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleColapsada(status); }}
                title={`Expandir ${STATUS_LABELS[status]} (${itens.length} · ${fmt(total)})`}
                className={'pk-fechada' + (ehAlvo ? ' pk-alvo' : '')}>
                <Text style={{ fontSize: 14, color: st.color }}>›</Text>
                <div style={{
                  marginTop: 8, writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: st.color,
                  fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  {STATUS_LABELS[status]} ({itens.length})
                </div>
              </div>
            );
          }

          const limite = visiveis[status] || LOTE;
          const mostrados = itens.slice(0, limite);
          const restantes = itens.length - mostrados.length;

          return (
            <div key={status} {...alvo(status)} className={'pk-col' + (ehAlvo ? ' pk-alvo' : '')}
              style={{ ['--pk-fundo' as any]: ehAlvo ? '#DCE5FB' : st.bg }}>
              <div className="pk-cab">
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                  {/* Caixa normal, não maiúsculas: "Identificação de Necessidades" em
                      maiúsculas espaçadas não cabia na coluna e virava reticências. */}
                  <Text strong ellipsis style={{ color: st.color, fontSize: 12.5, minWidth: 0, flex: 1 }}>
                    {STATUS_LABELS[status]}
                  </Text>
                  <Button size="small" type="text" onClick={() => toggleColapsada(status)} title="Recolher coluna"
                    style={{ fontSize: 12, color: st.color, padding: '0 4px', height: 18, lineHeight: 1 }}>‹</Button>
                </div>
                <Tooltip title={itens.length === 0 ? 'Nenhum negócio nesta etapa'
                  : `${itens.length} ${itens.length === 1 ? 'negócio' : 'negócios'} · ${fmt(total)}`
                    + (ehAberto(status) && ponderado > 0 ? ` · ponderado pela probabilidade ${fmt(ponderado)}` : '')}>
                  <div className="pk-cab-n">
                    <b>{itens.length}</b>
                    {total > 0 && <span style={{ color: '#5A6378' }}> · {valorCompacto(total)}</span>}
                  </div>
                </Tooltip>
              </div>

              <div className="pk-corpo">
                {itens.length === 0 && (
                  <Text type="secondary" italic style={{ fontSize: 11, textAlign: 'center', padding: '22px 4px' }}>
                    {arrastando ? 'Solte aqui' : 'Nenhum negócio nesta etapa'}
                  </Text>
                )}
                {mostrados.map(p => (
                  <Cartao key={p.id} p={p} passo={passos[p.id]} arrastando={arrastando === p.id}
                    onDragStart={() => setArrastando(p.id)}
                    onDragEnd={() => { setArrastando(null); setHoverCol(null); }}
                    onAbrir={() => onAbrir(p)} onEspiar={() => onEspiar(p)}
                    onIrComposicao={() => onIrComposicao(p)} onExcluir={() => onExcluir(p.id)} />
                ))}
                {restantes > 0 && (
                  <Button size="small" block onClick={() => setVisiveis(v => ({ ...v, [status]: limite + LOTE }))}>
                    Mostrar mais {Math.min(LOTE, restantes)} · faltam {restantes}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Enquanto se arrasta, Ganhar e Perder viram alvos grandes no rodapé. */}
      {arrastando != null && (
        <div className="pk-soltar" role="group" aria-label="Solte para fechar o negócio">
          <div {...alvo('ganho')} className={hoverCol === 'ganho' ? 'pk-sobre-ganho' : ''}>
            <CheckOutlined /> Ganhar — abre a venda
          </div>
          <div {...alvo('perdido')} className={hoverCol === 'perdido' ? 'pk-sobre-perdido' : ''}>
            <CloseOutlined /> Perder
          </div>
        </div>
      )}
    </>
  );
}

function Cartao({ p, passo, arrastando, onDragStart, onDragEnd, onAbrir, onEspiar, onIrComposicao, onExcluir }: {
  p: Proposta; passo?: ProximoPasso; arrastando: boolean;
  onDragStart: () => void; onDragEnd: () => void;
  onAbrir: () => void; onEspiar: () => void; onIrComposicao: () => void; onExcluir: () => void;
}) {
  const ehLicitacao = p.origem === 'licitacao';
  // L16 (2026-06-03): para licitação o prazo que importa é a ABERTURA do edital
  // (contador futuro com urgência), não o tempo parado.
  const urg = ehLicitacao && p.data_abertura && ehAberto(p.status)
    ? urgenciaAbertura(diasAte(p.data_abertura)) : null;
  const faixa = ehLicitacao ? '#722ED1' : COR_DO_NIVEL[nivelParado(p)];
  const parar = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };

  return (
    <div className={'pk-cartao' + (arrastando ? ' pk-arrastando' : '')}
      style={{ ['--pk-faixa' as any]: faixa, background: ehLicitacao ? '#FBF9FF' : undefined }}
      draggable onDragStart={onDragStart} onDragEnd={onDragEnd}
      onClick={onAbrir} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onAbrir(); }}>
      <div className="pk-acoes">
        <Tooltip title="Espiar sem sair do quadro">
          <Button size="small" type="text" icon={<EyeOutlined />} onClick={parar(onEspiar)} aria-label="Espiar" />
        </Tooltip>
        <Tooltip title="Composições">
          <Button size="small" type="text" icon={<BarChartOutlined />} onClick={parar(onIrComposicao)} aria-label="Composições" />
        </Tooltip>
        <Tooltip title="Excluir">
          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={parar(onExcluir)} aria-label="Excluir" />
        </Tooltip>
      </div>

      <div className="pk-titulo" title={p.titulo}>{p.titulo}</div>
      <div className="pk-cliente">{p.cliente_nome || <em>sem empresa</em>}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#020D7F', fontVariantNumeric: 'tabular-nums' }}>
          {fmt(valorDoNegocio(p))}
        </span>
        {ehAberto(p.status) && p.probabilidade != null && (
          <span style={{ fontSize: 10.5, color: '#5A6378' }}>{p.probabilidade}%</span>
        )}
        {ehLicitacao && (
          <Tag color="purple" style={{ fontSize: 9, margin: 0, padding: '0 4px', lineHeight: '14px' }}>Licitação</Tag>
        )}
        <span style={{ marginLeft: 'auto' }}><AvatarDono nome={p.responsavel} tamanho={20} /></span>
      </div>

      {ehAberto(p.status) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, paddingTop: 6,
          borderTop: '1px solid #F0F1F5', minWidth: 0, flexWrap: 'wrap' }}>
          {urg ? (
            <span style={{ fontSize: 11, color: urg.cor, fontWeight: urg.bold ? 700 : 400, whiteSpace: 'nowrap' }}>
              {urg.icone} abertura {urg.label}
            </span>
          ) : <SeloParado negocio={p} />}
          <SeloPasso negocio={p} passo={passo} curto />
        </div>
      )}
    </div>
  );
}
